import { Post, PostFilePayload } from "@/src/models/post.model";
import { pickFile, pickMedia } from "@/src/utils/picker-utils";
import { useEffect, useState, useCallback } from "react";
import { Platform } from "react-native";
import { useUploadFile } from "../use-upload-file";
import { SelectableItem } from "@/src/components/action-modals/item-selection/item-row";

export type VisibilityType = "public" | "group" | "specific" | "private";

export const usePostCreationModal = (
  post: Post | undefined,
  visible: boolean,
  onSubmit: (
    content: string,
    hashtags: string[],
    mentionedUsers: SelectableItem[],
    visibility: string,
    attachmentUrls: string[],
    visibleToGroupIds: string[],
    visibleToUserIds: string[],
    groupId?: string
  ) => void,
  onClose: () => void,
  isPostingFromParent: boolean = false,
  insideGroup?: boolean,
  groupId?: string
) => {
  // Form state
  const [content, setContent] = useState("");
  const [hashtags, setHashtags] = useState<string[]>([]);
  const [selectedMedia, setSelectedMedia] = useState<PostFilePayload[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<PostFilePayload[]>([]);
  const [showVisibilityModal, setShowVisibilityModal] = useState(false);
  const [showMentionModal, setShowMentionModal] = useState(false);
  const [visibility, setVisibility] = useState<VisibilityType>("public");
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [mentionedUsers, setMentionedUsers] = useState<SelectableItem[]>([]);

  const [isUploading, setIsUploading] = useState(false);

  const { mutateAsync: uploadFile } = useUploadFile();

  // Combined loading state
  const isLoading = isPostingFromParent || isUploading;

  useEffect(() => {
    if (post?.visibility) setVisibility(post.visibility as VisibilityType);
  }, [post]);

  useEffect(() => {
    switch (visibility) {
      case "group":
        setSelectedUserIds([]);
        break;
      case "specific":
        setSelectedGroupIds([]);
        break;
      default:
        setSelectedGroupIds([]);
        setSelectedUserIds([]);
    }
  }, [visibility]);

  // Populate form when modal opens or post changes
  useEffect(() => {
    if (visible) {
      const isGroupPost = Boolean(insideGroup && groupId);
      setContent(post?.content ?? "");
      setHashtags(post?.hashtags ?? []);
      setMentionedUsers(post?.mentionedUsers ?? []);
      setVisibility(
        (post?.visibility as VisibilityType) ??
          (isGroupPost ? "group" : "public"),
      );
      setSelectedGroupIds(
        post?.visibleToGroupIds ?? (isGroupPost ? [groupId!] : []),
      );
      setSelectedUserIds(post?.visibleToUserIds ?? []);

      // Split attachments into media and files
      const mediaUrls = post?.attachmentUrls?.filter((url) =>
        /\.(jpg|jpeg|png|gif|webp|mp4|mov|avi)$/i.test(url)
      ) ?? [];

      const fileUrls = post?.attachmentUrls?.filter((url) =>
        !/\.(jpg|jpeg|png|gif|webp|mp4|mov|avi)$/i.test(url)
      ) ?? [];

      setSelectedMedia(
        mediaUrls.map((url) => ({
          uri: url,
          fileName: url.split("/").pop() ?? "",
          type: /\.(mp4|mov|avi)$/i.test(url) ? "video/mp4" : "image/jpeg",
          file_size: 0,
        }))
      );

      setSelectedFiles(
        fileUrls.map((url) => ({
          uri: url,
          fileName: url.split("/").pop() ?? "",
          type: "application/octet-stream",
          file_size: 0,
        }))
      );
    }
  }, [visible, post]);

  const handleSubmit = async () => {
    if (isLoading) return;
    if (!content.trim() && !selectedMedia.length && !selectedFiles.length) return;

    setIsUploading(true);

    try {
      const allFiles = [...selectedMedia, ...selectedFiles];

      const attachmentUrls = await Promise.all(
        allFiles.map((file) =>
          uploadFile({
            uri: file.uri,
            fileName: file.fileName,
            mimeType: file.type,
          })
        )
      );

      onSubmit(
        content.trim(),
        hashtags,
        mentionedUsers,
        visibility,
        attachmentUrls as string[],
        selectedGroupIds,
        selectedUserIds,
        insideGroup && groupId ? groupId : undefined
      );

      // Reset form after successful submit
      resetForm();
    } catch (error) {
      console.error("Error uploading files:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const resetForm = () => {
    setContent("");
    setHashtags([]);
    setSelectedMedia([]);
    setSelectedFiles([]);
    setMentionedUsers([]);
    setSelectedGroupIds([]);
    setSelectedUserIds([]);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handlePickImage = async () => {
    const images = await pickMedia(false);
    if (images && Array.isArray(images)) {
      const mapped = images.map((img) => ({
        uri: img.uri,
        fileName: img.fileName as string,
        type: img.mimeType ?? "image/jpeg",
        file: Platform.OS === "web" ? (img as any).file : undefined,
        file_size: img.fileSize ?? 0,
      }));
      setSelectedMedia((prev) => [...prev, ...mapped]);
    }
  };

  const handleCameraPress = async () => {
    try {
      const image = await pickMedia(true);
      if (image) {
        const assets = Array.isArray(image) ? image : [image];
        const mapped = assets.map((img) => ({
          uri: img.uri,
          fileName: img.fileName ?? `image_${Date.now()}.jpg`,
          type: img.mimeType ?? "image/jpeg",
          file: Platform.OS === "web" ? (img as any).file : undefined,
          file_size: img.fileSize ?? 0,
        }));
        setSelectedMedia((prev) => [...prev, ...mapped]);
      }
    } catch (error) {
      console.error("Error taking photo:", error);
    }
  };

  const handlePickFile = async () => {
    try {
      const result = await pickFile(true, 10);
      if (result && !result.canceled && result.assets) {
        const mapped = result.assets.map((asset) => ({
          uri: asset.uri,
          fileName: asset.name,
          type: asset.mimeType ?? "application/octet-stream",
          file: Platform.OS === "web" ? (asset as any).file : undefined,
          file_size: asset.size ?? 0,
        }));
        setSelectedFiles((prev) => [...prev, ...mapped]);
      }
    } catch (error) {
      console.error("Error picking file:", error);
    }
  };

  const handleMentionUser = () => setShowMentionModal(true);

  const handleRemoveMedia = (index: number) => {
    setSelectedMedia((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveFile = (uri: string) => {
    setSelectedFiles((prev) => prev.filter((f) => f.uri !== uri));
  };

  const hasContent =
    content.trim().length > 0 ||
    selectedMedia.length > 0 ||
    selectedFiles.length > 0;

  return {
    content,
    setContent,
    hashtags,
    setHashtags,
    visibility,
    setVisibility,
    selectedMedia,
    selectedFiles,
    mentionedUsers,
    setMentionedUsers,
    showVisibilityModal,
    setShowVisibilityModal,
    showMentionModal,
    setShowMentionModal,
    selectedGroupIds,
    selectedUserIds,
    setSelectedGroupIds,
    setSelectedUserIds,
    hasContent,
    isLoading,           // ← Use this in the button
    handleSubmit,
    handleClose,
    handlePickImage,
    handleCameraPress,
    handlePickFile,
    handleMentionUser,
    handleRemoveMedia,
    handleRemoveFile,
  };
};
